/**
 * <TutorStudentActivitiesPage> — l'onglet Activités de la fiche d'un élève (page).
 *
 * La route `student` (`StudentTabsNavigator.js` → `TodoListScreen.js`, branche tuteur), sous l'en-tête
 * de la fiche : le bloc des activités (`<ActivitiesSection>`), avec tout ce que le tuteur y fait
 * aujourd'hui — relire, « Activités archivées », « Faites aujourd'hui » et « Ses 24 activités terminées »,
 * dépliées ici (`doneHistory`, l'écran `realizations` de l'ancien), toucher une activité (sa feuille), la sélection multiple et « Archiver (N) », ranger « À faire » au doigt
 * (`activityTodoIndexesAsTutor`), et « Générer une activité ». Le prochain contrôle de l'ancien écran
 * est dans l'Aperçu et l'onglet Contrôles.
 *
 * La voix est celle de l'élève (`voice="student"`) : le legacy lit `TEXTS.student` pour le tuteur
 * (« Lance une activité ! », « Ta liste est vide pour l'instant... »). Voir l'audit de parité.
 *
 * Contrôlée, comme `<ActivitiesPage>` : la sélection, l'archivage en cours, le glisser figé et les
 * états de page arrivent en props.
 */
import React from "react"

import { SelectionActionBar } from "../../molecules/SelectionActionBar"
import { ActivitiesSection, type ActivitiesSectionProps } from "../../templates/ActivitiesSection"
import { PageTemplate, PARENT_CONTENT_WIDTH } from "../../templates/PageTemplate"
import { countLabel } from "../../../ports/primary/ui/formatters/textFormatter"
import { ActivitiesSkeleton } from "../ActivitiesPage/ActivitiesSkeleton"

export type TutorStudentActivitiesPageProps = Omit<ActivitiesSectionProps, "voice"> & {
    onArchiveSelected?: () => void
    archiving?: boolean

    loading?: boolean
    error?: unknown
    onRetry?: () => void
}

/** Le lien des terminées, dit par le masteur de son élève. */
const historyLabel = (count: number) =>
    count === 1 ? "Son activité terminée" : `Ses ${countLabel(count, "activité terminée", "activités terminées")}`

export function TutorStudentActivitiesPage(props: TutorStudentActivitiesPageProps) {
    const { onArchiveSelected, archiving = false, loading = false, error, onRetry, testID, ...section } = props
    const { selectionMode = false, selectedIds = [], doneHistory } = section

    return (
        <PageTemplate
            testID={testID}
            refreshing={section.refreshing}
            onRefresh={section.onRefresh}
            loading={loading}
            skeleton={<ActivitiesSkeleton />}
            error={error}
            onRetry={onRetry}
            padding="4"
            maxContentWidth={PARENT_CONTENT_WIDTH}
            footer={
                selectionMode ? (
                    <SelectionActionBar
                        count={selectedIds.length}
                        actionLabel="Archiver"
                        iconName="archive"
                        color="danger"
                        onAction={onArchiveSelected}
                        loading={archiving}
                    />
                ) : undefined
            }
        >
            <ActivitiesSection
                {...section}
                voice="student"
                hideTitle
                doneTitle="Faites aujourd'hui"
                doneHistory={
                    doneHistory && {
                        ...doneHistory,
                        label: historyLabel(doneHistory.count),
                        hideLabel: "Masquer ses activités terminées",
                    }
                }
            />
        </PageTemplate>
    )
}
